import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faEdit,
  faExclamationTriangle,
  faGripVertical,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { Badge, Dropdown, Form, FormLabel, InputGroup } from "react-bootstrap";
import {
  Intervention,
  InterventionGroup,
  InterventionPlan,
  InterventionPlanItem,
  InterventionPlanStates,
  InterventionPlanStatesLabels,
} from "../../../../../../../store/onboarding/cases/types";
import { DraggableProvided } from "react-beautiful-dnd";
import {
  addInterventionsToGroup,
  hideInterventionPlanModal,
  openCreateNewInterventionModal,
  openInterventionLibraryModal,
  patchInterventionGroup,
  showInterventionPlanModal,
} from "../../../../../../../store/onboarding/cases/actions";
import { useDispatch } from "react-redux";
import { checkedInterventionPlan, removeFromInterventionPlan } from "../../../../../../utils";
import { showConfirmDialog } from "../../../../../../../store/confirm-dialog/actions";
import { Portal } from "react-portal";
import { toastr } from "react-redux-toastr";
import useInterventionGroupAccess from "../../../../../../../utils/hooks/useInterventionGroupAccess";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type Props = {
  //provided: DraggableProvided;
  index: number;
  statePlan: InterventionPlan;
  intervention: Intervention;
  interventionGroup: InterventionGroup;
  isLoading?: boolean;
  setStatePlan: React.Dispatch<React.SetStateAction<InterventionPlan>>;
  hideDelete: boolean;
  isStudentDetail: boolean;
  isGroupDetail: boolean;
  isDragOverlay?: boolean;   // ✅ added
  interventionCount?: number;
  isDragPermitted?: boolean;
};

const CustomToggle = (interventionState?: InterventionPlanStates) =>
  React.forwardRef((props: any, ref: any) => (
    <Badge
      ref={ref}
      onClick={props.onClick}
      pill
      variant={
        interventionState === InterventionPlanStates.ACTIVE
          ? "primary"
          : interventionState === InterventionPlanStates.COMPLETED
          ? "success"
          : interventionState === InterventionPlanStates.DISCONTINUED
          ? "warning"
          : "secondary"
      }
      className="d-flex align-items-center justify-content-center pointer"
    >
      <h4 className="text-white mr-1">
        {interventionState
          ? InterventionPlanStatesLabels[interventionState]
          : "Not selected"}
      </h4>
      <FontAwesomeIcon
        style={{ color: "white" }}
        icon={props["aria-expanded"] ? faChevronUp : faChevronDown}
      />
    </Badge>
  ));

const InterventionElement: FunctionComponent<Props> = (props) => {
  const {
    //provided,
    index,
    isLoading,
    statePlan,
    setStatePlan,
    intervention,
    interventionGroup,
    hideDelete,
    isStudentDetail,
    isGroupDetail,
    interventionCount,
    isDragPermitted,
    isDragOverlay = false,
  } = props;
  const { interventions } = interventionGroup;
  const [statePreviousPlan, setStatePreviousPlan] = useState<InterventionPlan>(
      checkedInterventionPlan(interventions, interventionGroup.intervention_plan)
  );
  const {
    attributes,       // aria + role attributes for accessibility
    listeners,        // pointer/keyboard event listeners for drag handle
    setNodeRef,       // ref callback — replaces provided.innerRef
    transform,        // current drag transform (x/y/scaleX/scaleY)
    transition,       // CSS transition string during drag
    isDragging,       // true while this item is being dragged
  } = useSortable({
    id: intervention.id.toString(),
    disabled: isLoading || isDragOverlay, 
  });

  const style: React.CSSProperties = isDragOverlay
    ? {
        // ✅ Overlay clone: solid, elevated appearance
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        cursor: !isLoading ? "grabbing" : "default",
        opacity: 1,
      }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        // ✅ Original slot: invisible but still occupies space (no layout collapse)
        opacity: isDragging ? 0.5 : 1,
        visibility: "visible",
      };
  useEffect(() => {
    const updatedPlan = checkedInterventionPlan(
      interventions,
      interventionGroup.intervention_plan
    );

    setStatePreviousPlan(updatedPlan);
  }, [interventions, interventionGroup.intervention_plan]);
  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(interventionGroup.id);

  const dispatch = useDispatch();

  const planItem = useMemo(
    () =>
      statePlan.plan_items?.find(
        (pi) => pi.intervention_id === intervention.id
      ),
    [statePlan.plan_items, intervention.id]
  );

  const handleDelete = (intervention: Intervention) => {
    if (interventions.length > 1) {
      dispatch(
        showConfirmDialog({
          onConfirm: () => onRemove(intervention),
          title: "Delete Intervention",
          text: (
            <>
              <p className="font-weight-bold">Are you sure you want to delete this intervention?</p>
              <p className="text-center">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle}
                  style={{color:'red'}}
                /> {" "}
                <i>This action cannot be undone.</i>
              </p>
            </>
          ),
          confirmButtonText: "DELETE",
          centered: true,
        })
      );
    } else {
      toastr.error(
        "Error",
        "This is the only intervention in the group. It cannot be deleted."
      );
    }
  };

  const onRemove = (intervention: Intervention) => {
    if (interventionGroup.id) {
      const changedPlan = removeFromInterventionPlan(
        intervention,
        interventions,
        statePlan
      );

      const changedInterventionsList = interventions.filter(
        (int) => int !== intervention
      );

      dispatch<any>(addInterventionsToGroup(
        interventionGroup.id,
        changedInterventionsList.map((int) => int.id)
      )).then(() => {
        if (interventionGroup.id) {
          dispatch(
            patchInterventionGroup(interventionGroup.id, {
              intervention_plan: changedPlan,
            }, true)
          );
          setStatePlan(changedPlan);
          // if (changedInterventionsList.length === 0) {
          //   dispatch(hideInterventionPlanModal());
          // }
        }
      });
    }
  };

  const [discontinueReason, setDiscontinueReason] = useState<string>();
  const [showDiscontinueForm, setShowDiscontinueForm] = useState<boolean>(
    false
  );

  const onChangeInterventionState = (planItem?: InterventionPlanItem) => (
    eventKey: string | null
  ) => {
    if (interventionGroup.id && planItem) {
      const isDiscontinue = eventKey === InterventionPlanStates.DISCONTINUED;

      if (isDiscontinue) {
        const currentPlan: InterventionPlan = {
          plan_items: statePlan.plan_items?.map((pi) =>
            pi.intervention_id === planItem?.intervention_id
              ? {
                  ...pi,
                  state: planItem.state,
                }
              : pi
          ),
        };
        setStatePreviousPlan(currentPlan);
        setShowDiscontinueForm(true);
        const changedPlan: InterventionPlan = {
          plan_items: statePlan.plan_items?.map((pi) =>
            pi.intervention_id === planItem?.intervention_id
              ? {
                  ...pi,
                  state: InterventionPlanStates.DISCONTINUED,
                  discontinue_reason: discontinueReason,
                }
              : pi
          ),
        };
    
        setStatePlan(changedPlan);
      } else {
        const changedPlan: InterventionPlan = {
          plan_items: statePlan.plan_items?.map((pi) =>
            pi.intervention_id === planItem.intervention_id
              ? {
                  ...pi,
                  state: eventKey as InterventionPlanStates,
                  discontinue_reason: isDiscontinue
                    ? pi.discontinue_reason ?? ""
                    : undefined,
                }
              : pi
          ),
        };

        setStatePlan(changedPlan);
        setShowDiscontinueForm(false);
        dispatch(
          patchInterventionGroup(interventionGroup.id, {
            intervention_plan: changedPlan,
          })
        );
      }
    }
  };

  document.body.blur();

  const onChangeDiscontinueReason = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    setDiscontinueReason(value);
  };

  const onKeydownDiscontinueReason = (event: React.KeyboardEvent) => {
    if (
      event.key === "Enter" &&
      interventionGroup.intervention_plan !== statePlan
    ) {
      handleInterventionDiscontinue();
    }
  };

  const handleInterventionDiscontinue = () => {
    const changedPlan: InterventionPlan = {
      plan_items: statePlan.plan_items?.map((pi) =>
        pi.intervention_id === planItem?.intervention_id
          ? {
              ...pi,
              state: InterventionPlanStates.DISCONTINUED,
              discontinue_reason: discontinueReason,
            }
          : pi
      ),
    };

    setStatePlan(changedPlan);

    (dispatch(
      patchInterventionGroup(interventionGroup.id, {
        intervention_plan: changedPlan,
      })
    ) as any).then(() => setShowDiscontinueForm(false));
  };

  const handleModifyIntervention = () => {
    dispatch(
      showConfirmDialog({
        title: "Modify Intervention",
        text: (
          <>
            <p className="text-center font-weight-bold">Are you sure you want to modify this intervention?</p>
            <p className="text-center">Doing so will create a new copy and add it to your list.</p>
          </>
        ),
        onConfirm: () => {
          dispatch(openCreateNewInterventionModal(
            intervention,
            interventionGroup?.id
          ));
        },
        confirmButtonText: "Continue",
      })
    );
  }

  const handleCancelDiscontinue = () => {
    if (interventionGroup.id && planItem) {
      setStatePlan(statePreviousPlan!);
      setShowDiscontinueForm(false);
    }
  }

  return (
    <div
      onClick={() => {
        if(!isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess) {
          dispatch(hideInterventionPlanModal());
          dispatch(
            openInterventionLibraryModal(undefined, intervention, {
              onBack: () => {
                dispatch(showInterventionPlanModal());
              },
            })
          );
        }
      }}
      className="intervention-plan-item"
       ref={isDragOverlay ? undefined : setNodeRef} 
      style={style}
    >
      <div className="w-100 d-flex flex-column">
        <div className="drag-handle" style={{ cursor: isLoading || !isDragPermitted ? "default" : isDragging ? 'grabbing' : "pointer" }}
          {...(isDragOverlay || !isDragPermitted ? {} : listeners)}      // ✅ no listeners for overlay
          {...(isDragOverlay ? {} : attributes)}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1 mr-3" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-start">
                {interventionCount && (interventionCount > 1) && !isLoading && isDragPermitted ? (
                  <FontAwesomeIcon
                    icon={faGripVertical}
                    className="mr-2 mt-1 flex-shrink-0"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                  />
                ) : null}

                <span className="mr-1 flex-shrink-0 font-weight-bold">
                  {index + 1}.
                </span>

                <span className="font-weight-bold">
                  {intervention.name}
                </span>
              </div>

              {!isStudentDetail &&
                !isGroupDetail &&
                !isIntGrpIDsWithReadAccess && (
                  <div className="mt-2 ml-2">
                    <Badge
                      variant="secondary"
                      pill
                      className="pointer p-2"
                      style={{ fontSize: "10px" }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        handleModifyIntervention();
                      }}
                    >
                      MODIFY INTERVENTION
                    </Badge>
                  </div>
                )}
            </div>
            <div className="d-flex align-items-start flex-shrink-0">
              {!isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess ? 
                <Dropdown
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                  className="m-1 recommendationsSelectInterventions"
                  onSelect={onChangeInterventionState(planItem)}
                  >
                  <Dropdown.Toggle
                    id="dropdown-basic"
                    as={CustomToggle(planItem?.state)}
                  />
                  <Portal>
                    <Dropdown.Menu style={{ zIndex: 9999 }}>
                      <Dropdown.Item
                        active={planItem?.state === InterventionPlanStates.ACTIVE}
                        eventKey={InterventionPlanStates.ACTIVE}
                        as="button"
                      
                      >
                        Active
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={planItem?.state === InterventionPlanStates.COMPLETED}
                        eventKey={InterventionPlanStates.COMPLETED}
                        as="button"
                      >
                        Completed
                      </Dropdown.Item>
                      <Dropdown.Item
                        active={
                          planItem?.state === InterventionPlanStates.DISCONTINUED
                        }
                        eventKey={InterventionPlanStates.DISCONTINUED}
                        as="button"
                      >
                        Discontinued
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Portal>
                </Dropdown>
              : 
                <FormLabel>{planItem!.state!.charAt(0).toUpperCase() + planItem!.state!.slice(1)}</FormLabel>
              }
              {!isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess && 
              (interventionCount && interventionCount > 1) ?
              <div
                className="m-1 mt-2"
                onClick={(e: any) => {
                  e.stopPropagation();
                  !isLoading && handleDelete(intervention);
                  //!hideDelete && !isLoading && handleDelete(intervention);
                }}
                //style={{ visibility: hideDelete ? "hidden" : "visible" }}
              >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  size="1x"
                  className="pointer"
                  style={{color:'red'}}
                  title="Delete"
                />
              </div> : null
              }
            </div>
          </div>
          <div
            onClick={(e: any) => {
              e.stopPropagation();
            }}
          >
            {(planItem?.state === InterventionPlanStates.DISCONTINUED ||
              showDiscontinueForm) &&
              (showDiscontinueForm ? (
                <>
                  <hr className="w-100 mx-0 my-1" />
                  <h5 className="mb-1 font-weight-bold">
                    Why are you discontinuing this intervention?
                  </h5>
                  <div className="d-flex">
                    <InputGroup size="sm">
                      <Form.Control
                        value={discontinueReason ?? ""}
                        onChange={onChangeDiscontinueReason}
                        onKeyDown={onKeydownDiscontinueReason}
                      />
                    </InputGroup>
                    <button
                      className="blueBtnSm ml-3"
                      onClick={handleInterventionDiscontinue}
                    >
                      Save
                    </button>
                    <button
                      className="whiteBtnSm ml-3"
                      onClick={handleCancelDiscontinue}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <hr className="w-100 mx-0 my-1" />
                  <div className="ml-3">
                    <h5 className="mb-1 font-weight-bold">
                      Discontinue reason{" "}
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowDiscontinueForm(true)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </span>{" "}
                      :
                    </h5>
                    <p
                      className="mb-0"
                      style={{ fontSize: "small", lineHeight: "normal" }}
                    >
                      {planItem?.discontinue_reason ? (
                        planItem?.discontinue_reason
                      ) : (
                        <i>No reason was given</i>
                      )}
                    </p>
                  </div>
                </>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterventionElement;
